import * as React from "react";
import QrReader from "react-qr-reader";
import { view } from "../../lib/vvm";
import { Layout } from "../../components/Layout/Layout";
import styles from "./AddBoxPage.module.css";
import { EmptyImage } from "../../components/EmptyImage/EmptyImage";
import Button, { ButtonDesign } from "../../components/Button/Button";
import FieldSet from "../../components/FieldSet/FieldSet";
import Input from "../../components/Input/Input";
import { TypeTagsInput } from "../../components/TypeTagsInput/TypeTagsInput";
import { ConfirmDialog } from "../../components/ConfirmDialog/ConfirmDialog";
import {
  AddBoxPageViewModel,
  type AddBoxPageViewProps,
} from "./AddBoxPageViewModel";

interface AddBoxPageProps extends AddBoxPageViewProps {}

export const AddBoxPage: React.FC<AddBoxPageProps> = view(
  AddBoxPageViewModel,
)<AddBoxPageProps>(({
  viewModel: {
    title,
    photo,
    name,
    description,
    typeTags,
    typeTagInput,
    parentId,
    qrPayloadInput,
    qrBindingError,
    isQrScannerOpen,
    isQrScanResolving,
    qrScannerDelay,
    error,
    referenceError,
    isReferenceLoading,
    isPhotoLoading,
    typeTagSuggestions,
    parentIdSuggestions,
    isSubmitting,
    canSubmit,
    voiceTranscript,
    isVoicePanelOpen,
    isVoiceSupported,
    isVoiceListening,
    isVoiceApplying,
    voiceError,
    voiceInfo,
    canStartVoiceListening,
    canStopVoiceListening,
    canApplyVoiceTranscript,
    canApplyPhotoAutofill,
    isAiPhotoAutofillEnabled,
    isAiPhotoSuggestionOpen,
    aiPhotoSuggestionDescription,
    onNameChange,
    onDescriptionChange,
    onPhotoChange,
    onPhotoFileChange,
    onVoiceTranscriptChange,
    onVoicePrimaryButtonClick,
    closeVoicePanel,
    clearVoiceTranscript,
    setTypeTagInput,
    addTypeTags,
    removeTypeTag,
    onParentIdChange,
    toggleQrScanner,
    onQrScan,
    onQrScanError,
    startVoiceListening,
    stopVoiceListening,
    fillFromVoiceTranscript,
    fillFromPhoto,
    applyAiPhotoSuggestion,
    cancelAiPhotoSuggestion,
    submit,
    clearForm,
    goBack,
    backButtonLabel,
    hasPhotoPreview,
  },
}) => {
  return (
    <Layout title={title}>
      <div className={styles.root}>
        {hasPhotoPreview ? (
          <img
            src={photo}
            alt={name || "preview"}
            className={styles.image}
            decoding="async"
          />
        ) : (
          <EmptyImage />
        )}

        <FieldSet title={"наименование"}>
          <Input
            value={name}
            onChange={onNameChange}
            placeholder="Например: Коробка для инструментов"
          />
        </FieldSet>

        <FieldSet title={"описание"}>
          <Input
            value={description}
            onChange={onDescriptionChange}
            placeholder="Короткое описание"
          />
        </FieldSet>

        <FieldSet title={"голосовой ввод (AI-черновик)"}>
          <div className={styles.voiceHeader}>
            <button
              type="button"
              className={`${styles.voiceCircleButton} ${isVoiceListening ? styles.voiceCircleButtonActive : ""}`}
              onClick={() => onVoicePrimaryButtonClick()}
              disabled={isVoiceApplying || isSubmitting}
              aria-pressed={isVoiceListening}
              aria-label={
                isVoiceListening
                  ? "Остановить голосовой ввод"
                  : "Начать голосовой ввод"
              }
            >
              <span className={styles.voiceCircleCore} />
            </button>
            <div className={styles.voiceHeaderText}>
              <p className={styles.voiceTitle}>
                {isVoiceListening
                  ? "Слушаем речь"
                  : "Нажмите круглую кнопку и диктуйте"}
              </p>
              <p className={styles.voiceSubtitle}>
                {isVoiceListening
                  ? "Текст будет добавляться автоматически"
                  : "Можно также вставить или ввести текст вручную"}
              </p>
            </div>
            {isVoicePanelOpen && (
              <Button design={ButtonDesign.ghost} onClick={closeVoicePanel}>
                скрыть
              </Button>
            )}
          </div>

          {isVoicePanelOpen && (
            <>
              <div className={styles.voiceStatus}>
                <span
                  className={`${styles.voiceStatusDot} ${isVoiceListening ? styles.voiceStatusDotActive : ""}`}
                />
                {isVoiceListening ? "микрофон активен" : "микрофон выключен"}
              </div>
              <textarea
                className={styles.voiceTextarea}
                value={voiceTranscript}
                onChange={onVoiceTranscriptChange}
                placeholder={
                  isVoiceSupported
                    ? "Продиктуйте текст или введите его вручную"
                    : "Браузер не поддерживает голосовой ввод. Введите текст вручную."
                }
                rows={5}
              />
              <div className={styles.voiceButtons}>
                <Button
                  design={ButtonDesign.secondary}
                  onClick={() =>
                    isVoiceListening
                      ? stopVoiceListening()
                      : startVoiceListening()
                  }
                  disabled={
                    isVoiceListening
                      ? !canStopVoiceListening
                      : !canStartVoiceListening
                  }
                >
                  {isVoiceListening ? "пауза" : "продолжить диктовку"}
                </Button>
                <Button
                  design={ButtonDesign.primary}
                  onClick={() => void fillFromVoiceTranscript()}
                  disabled={!canApplyVoiceTranscript}
                  isLoading={isVoiceApplying}
                >
                  заполнить форму
                </Button>
                <Button
                  design={ButtonDesign.basicGray}
                  onClick={clearVoiceTranscript}
                  disabled={!voiceTranscript.trim()}
                >
                  очистить текст
                </Button>
              </div>
            </>
          )}
          {voiceInfo && <p className={styles.hint}>{voiceInfo}</p>}
          {voiceError && <p className={styles.error}>{voiceError}</p>}
        </FieldSet>

        <FieldSet title={"photo (URL)"}>
          <Input
            value={photo}
            onChange={onPhotoChange}
            placeholder="https://..."
          />
          <input
            type="file"
            accept="image/*"
            onChange={(event) => void onPhotoFileChange(event)}
            className={styles.fileInput}
          />
          {isPhotoLoading && (
            <p className={styles.hint}>загружаем изображение...</p>
          )}
          <Button
            design={ButtonDesign.primary}
            onClick={() => void fillFromPhoto()}
            disabled={!canApplyPhotoAutofill}
            isLoading={isVoiceApplying}
          >
            заполнить по фото
          </Button>
          <p className={styles.hint}>
            Если есть текст диктовки, AI объединит фото и текст в один черновик.
          </p>
          {!isAiPhotoAutofillEnabled && (
            <p className={styles.hint}>
              AI-по-фото выключен в настройках приложения.
            </p>
          )}
        </FieldSet>

        <FieldSet title={"типы (#теги)"}>
          <TypeTagsInput
            tags={typeTags}
            suggestions={typeTagSuggestions}
            inputValue={typeTagInput}
            onInputValueChange={setTypeTagInput}
            onAddTags={addTypeTags}
            onRemoveTag={removeTypeTag}
            datalistId="add-box-type-tags"
            placeholder="#продукты #быт"
          />
        </FieldSet>

        <FieldSet title={"коробка-родитель (опционально)"}>
          <Input
            value={parentId}
            onChange={onParentIdChange}
            placeholder="Например: Склад"
            list="add-box-parent-ids"
          />
          <datalist id="add-box-parent-ids">
            {parentIdSuggestions.map((box) => (
              <option key={box.id} value={box.label} />
            ))}
          </datalist>
        </FieldSet>

        <FieldSet title={"QR-код (опционально)"}>
          <div className={styles.qrControls}>
            <Button
              design={
                isQrScannerOpen ? ButtonDesign.secondary : ButtonDesign.basic
              }
              onClick={toggleQrScanner}
              disabled={isSubmitting || isQrScanResolving}
            >
              {isQrScannerOpen ? "Остановить сканирование" : "Сканировать QR"}
            </Button>
          </div>

          {isQrScannerOpen && (
            <div className={styles.qrScannerWrap}>
              <QrReader
                delay={qrScannerDelay}
                facingMode="environment"
                onError={onQrScanError}
                onScan={(value) => void onQrScan(value)}
                className={styles.qrReader}
                style={{ width: "100%" }}
              />
              <div className={styles.qrOverlayFrame} />
            </div>
          )}

          <p className={styles.hint}>
            {qrPayloadInput
              ? `Выбран QR: ${qrPayloadInput}`
              : "Отсканируйте QR из новой партии, чтобы привязать его к коробке после создания."}
          </p>
          {isQrScanResolving && <p className={styles.hint}>проверяем QR...</p>}
          {qrBindingError && <p className={styles.error}>{qrBindingError}</p>}
        </FieldSet>

        {isReferenceLoading && (
          <p className={styles.hint}>загружаем коробки и теги...</p>
        )}
        {referenceError && <p className={styles.error}>{referenceError}</p>}
        {error && <p className={styles.error}>{error}</p>}

        <div className={styles.buttons}>
          <Button
            design={ButtonDesign.primary}
            onClick={() => void submit()}
            disabled={!canSubmit}
            isLoading={isSubmitting}
          >
            создать
          </Button>
          <Button design={ButtonDesign.basic} onClick={clearForm}>
            очистить
          </Button>
        </div>

        <Button design={ButtonDesign.ghost} onClick={goBack}>
          {backButtonLabel}
        </Button>
      </div>
      <ConfirmDialog
        isOpen={isAiPhotoSuggestionOpen}
        title="Применить AI-правки?"
        description={aiPhotoSuggestionDescription}
        confirmLabel="применить"
        cancelLabel="оставить как есть"
        tone="neutral"
        onConfirm={applyAiPhotoSuggestion}
        onCancel={cancelAiPhotoSuggestion}
      />
    </Layout>
  );
});
